/**
 * @fileoverview Organization creation step for onboarding.
 * @module features/onboarding/components/steps/CreateOrgStep
 */

import { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Loader2 } from 'lucide-react'
import { i18next } from '@/lib/i18n'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollReveal } from '@/shared/components/ui'
import { createOrg } from '@/features/orgs/api/orgsService'
import { switchOrg } from '@/features/preferences/api/preferencesService'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'

/** Schema for organization name validation */
const createOrgSchema = z.object({
  name: z.string().min(1, 'Organization name is required').max(100),
})

interface CreateOrgStepProps {
  /** Callback when organization is created successfully */
  onComplete: (orgId: string, orgName: string) => void
}

/**
 * Second step of onboarding - creates the user's organization.
 *
 * @component
 * @example
 * ```tsx
 * <CreateOrgStep onComplete={(id, name) => {
 *   setOrgData(id, name)
 *   completeStep()
 * }} />
 * ```
 */
export function CreateOrgStep({ onComplete }: CreateOrgStepProps): ReactElement {
  const { t } = useTranslation('features-onboarding')
  const { refreshFromProfile } = useActiveOrg()
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const parsed = createOrgSchema.safeParse({ name })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Invalid name')
      setLoading(false)
      return
    }

    try {
      const org = await createOrg(parsed.data.name)
      // Preserve the user's current language selection when creating their first preference record
      const profile = await switchOrg({
        organizationId: org.id,
        preferredLanguage: i18next.language,
      })

      // Hydrate org context
      refreshFromProfile({
        organizations: profile.organizations,
        defaultOrgId: profile.defaultOrgId ?? null,
        ...(profile.preferredLanguage ? { preferredLanguage: profile.preferredLanguage } : {}),
      })

      // Notify parent of success
      onComplete(org.id, parsed.data.name)
    } catch (err) {
      const anyErr = err as { message?: string }
      setError(anyErr?.message ?? 'Failed to create organization')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col items-center justify-center max-w-md mx-auto">
      {/* Heading */}
      <ScrollReveal animateOnMount blur delay={0}>
        <h2 className="text-3xl font-bold tracking-tight text-foreground mb-4 text-center">
          {t('createOrg.title')}
        </h2>
      </ScrollReveal>

      <ScrollReveal animateOnMount blur delay={0.1}>
        <p className="text-muted-foreground mb-8 text-center">
          {t('createOrg.hint')}
        </p>
      </ScrollReveal>

      {/* Form */}
      <ScrollReveal animateOnMount blur delay={0.2} className="w-full">
        <form onSubmit={handleSubmit} className="w-full space-y-6">
          {error && (
            <div className="rounded-lg bg-destructive p-3 text-sm text-white">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="org-name" className="sr-only">
              {t('createOrg.label')}
            </Label>
            <Input
              id="org-name"
              name="name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t('createOrg.placeholder')}
              className="text-center text-lg h-12"
              autoFocus
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
              required
            />
          </div>

          <Button type="submit" variant="outline" size="lg" className="w-full" disabled={loading || !name.trim()}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('createOrg.creating')}
              </>
            ) : (
              <>
                {t('createOrg.cta')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </form>
      </ScrollReveal>
    </div>
  )
}
